import { FC, useContext } from "react";

import { AvailablePaymentCurrency, formatSmartNarrowCurrencyFromCents, FormType } from "@simplyk/common";
import { UseFormReturn, useController, useFieldArray } from "react-hook-form";

import { useFrontendAuctionContext } from "@/contexts/FrontendAuctionContext";
import { FrontendTicketingContext } from "@/contexts/FrontendTicketingContext";
import { useLocaleContext } from "@/contexts/LocaleContext";
import { useFormV2Context } from "@/features/FormV2/context/FormV2Context";
import { initAnswer } from "@/features/FormV2/context/TicketingV2Provider";
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/context/PreviewContext";
import { FormAmountField } from "@/forms-design-system/FormAmountField";
import { isMin } from "@/helpers/validators";
import { useTranslate } from "@/hooks/useTranslate";
import { ParticipantAnswers, TicketingPaymentInput } from "@/types/ticketing";
import { RateOutput, QuestionOutput } from "@/types/trpc";

interface AuctionInputProps {
  rate: RateOutput;
  indexInDisplayedRates: number;
  onBidChange: (bid: string) => void;
}

export const AuctionInput = ({ rate, indexInDisplayedRates, onBidChange }: AuctionInputProps) => {
  const { questions, form, formCurrency } = useFormV2Context(FormType.Ticketing);
  const { highestBidsMapByRateId } = useFrontendAuctionContext();
  const { ticketingIsClosed } = useContext(FrontendTicketingContext);
  const { isPreview } = usePreviewContext();

  const highestBid = highestBidsMapByRateId?.[rate.id]?.amount;
  const hasABid = Boolean(highestBid);
  // this is to avoid the case where the highest bid is lower than the rate amount, for example if the org changes the rate amount after a bid has been placed
  const currentBid = Math.max(highestBid ?? -Infinity, rate.amount);
  const currentBidIncrement = (hasABid ? rate.bidIncrement : 0) || 0;
  const minimumBid = currentBid + currentBidIncrement;

  return (
    <StatelessAuctionInput
      rate={{ ...rate, minimumBid }}
      indexInDisplayedRates={indexInDisplayedRates}
      ticketingIsClosed={ticketingIsClosed}
      form={form}
      formCurrency={formCurrency}
      questions={questions}
      onBidChange={onBidChange}
      isPreview={isPreview}
    />
  );
};

type StatelessAuctionInputProps = {
  rate: RateOutput & { minimumBid: number };
  indexInDisplayedRates: number;
  ticketingIsClosed: boolean;
  form: UseFormReturn<TicketingPaymentInput>;
  formCurrency: AvailablePaymentCurrency;
  questions: QuestionOutput[];
  onBidChange: (value: string) => void;
  isPreview: boolean;
};

export const StatelessAuctionInput: FC<StatelessAuctionInputProps> = ({
  rate,
  indexInDisplayedRates,
  ticketingIsClosed,
  form,
  formCurrency,
  questions,
  onBidChange,
  isPreview,
}) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();

  const { control, register } = form;
  const { errors } = form.formState;

  const formattedMinimumBid = rate.minimumBid
    ? formatSmartNarrowCurrencyFromCents(rate.minimumBid, {
        locale,
        currency: formCurrency,
      })
    : "$ -";

  const { fields, remove, update } = useFieldArray({
    control,
    name: "customAnswersOfParticipants",
  });

  const {
    field: { ref: bidAmountFieldRef, value: bidAmountFieldValue, onChange: onBidAmountFieldChange, ...bidAmountField },
  } = useController({
    control,
    name: `ticketsPurchased.${indexInDisplayedRates}.bid`,
    rules: isMin(
      rate.minimumBid,
      t("ticketing", "auction.input.minError", {
        amount: formattedMinimumBid,
      })
    ),
  });

  const onChange = (value: number | null) => {
    onBidAmountFieldChange(value);

    if (value) {
      onBidChange(value.toString());
      // Check if the rate already exists in customAnswers
      const isAlreadyPresent = fields[indexInDisplayedRates]?.rateId;

      if (!isAlreadyPresent) {
        const newCustomAnswers: ParticipantAnswers = {
          rateId: rate.id,
          answers: questions.filter((question) => question.hasRate === true).map(initAnswer),
        };

        // Insert the new custom answer at the correct rate index
        update(indexInDisplayedRates, newCustomAnswers);
      }
    } else {
      // If the value goes from defined to undefined, remove the rate from the custom answers
      remove(indexInDisplayedRates);
    }
  };

  return (
    <>
      <FormAmountField
        {...bidAmountField}
        inputRef={bidAmountFieldRef}
        disabled={ticketingIsClosed}
        placeholder={t("ticketing", "auction.input.placeholder", {
          amount: formattedMinimumBid,
        })}
        error={!!errors?.ticketsPurchased?.[indexInDisplayedRates]?.bid?.message}
        helperText={errors?.ticketsPurchased?.[indexInDisplayedRates]?.bid?.message}
        data-test="bid-input"
        disableInjectingGlobalStyles={isPreview} // We need this to make the Live Form Editor smooth on local
        hideAdornment
        value={bidAmountFieldValue ?? null}
        onChange={onChange}
      />

      <input
        type="hidden"
        value={rate.id || undefined}
        {...register(`ticketsPurchased.${indexInDisplayedRates}.rateId`)}
        readOnly
      />
    </>
  );
};
